import { formatDistanceToNow } from "date-fns";
import { ActivityLogEntry } from "@/hooks/useBusinesses";

const ACTION_LABELS: Record<string, string> = {
  BUSINESS_CREATED: "Created business",
  BUSINESS_APPROVED: "Approved",
  BUSINESS_REJECTED: "Rejected",
  BUSINESS_SUSPENDED: "Suspended",
  BUSINESS_REACTIVATED: "Reactivated",
  BUSINESS_VISIBILITY_ON: "Turned visibility ON",
  BUSINESS_VISIBILITY_OFF: "Turned visibility OFF",
  BUSINESS_DELETED: "Deleted business",
  BUSINESS_UPDATED: "Updated business",
  DOCUMENT_VIEWED: "Viewed document",
  DOCUMENT_UPLOADED: "Uploaded document",
  DOCUMENT_DELETED: "Deleted document",
  OWNER_BULK_TOGGLE_ON: "Turned ON all owner businesses",
  OWNER_BULK_TOGGLE_OFF: "Turned OFF all owner businesses",
};

interface Props {
  entries: ActivityLogEntry[];
  emptyText?: string;
}

function describe(entry: ActivityLogEntry): string {
  const label = ACTION_LABELS[entry.action] ?? entry.action;
  const meta = entry.metadata as any;
  if (!meta) return label;

  if (entry.action === "BUSINESS_REJECTED" && Array.isArray(meta.reasons)) {
    return `${label} — ${meta.reasons.length} reason(s)${meta.note ? ` · note` : ""}`;
  }
  if (entry.action.startsWith("DOCUMENT_") && meta.docType) {
    return `${label} (${meta.docType})`;
  }
  if (
    entry.action.startsWith("OWNER_BULK_") &&
    typeof meta.affectedBusinessCount === "number"
  ) {
    return `${label} — affected ${meta.affectedBusinessCount} business(es)`;
  }
  return label;
}

export const ActivityTimeline = ({
  entries,
  emptyText = "No activity yet.",
}: Props) => {
  if (!entries || entries.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">{emptyText}</p>;
  }

  return (
    <ol className="space-y-3">
      {entries.map((e) => (
        <li
          key={e.id}
          className="flex gap-3 pb-3 border-b border-border last:border-0"
        >
          <div className="w-2 h-2 mt-2 rounded-full bg-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{describe(e)}</p>
            <p className="text-xs text-muted-foreground">
              {e.admin.name ?? e.admin.email ?? "Admin"} ·{" "}
              {formatDistanceToNow(new Date(e.createdAt), { addSuffix: true })}
            </p>
            {(e.metadata as any)?.note ? (
              <p className="text-xs mt-1 italic text-muted-foreground">
                “{String((e.metadata as any).note)}”
              </p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
};
